import type { PayloadAction, Slice } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { DeviceEventResponse } from "@somewear/api";
import {
	DeviceSettings,
	DeviceSettingsEventType,
	DeviceSettingsResponse,
	DeviceStatusResponse,
	OptionalBool,
	OutboundGcStrategy,
	SettingsCommand,
} from "@somewear/api";
import {
	type ButtonFunctionMap,
	InmarsatLeaseType,
	RadioPowerMode,
	type RadioPowerModeMap,
} from "@somewear/api/src/proto/command_public_proto_pb";
import { emitAddUserAccountFromServer, emitUserAccountChangeFromServer } from "@somewear/asset";
import type { IDeviceTransfer } from "@somewear/device";
import { apiDeviceRecordUpdate, deviceActions, deviceTransferActions } from "@somewear/device";
import type { Dict, Dictionary, ValueOf } from "@somewear/model";
import { deviceHasNodeSerial, Sentry, toOptionalBool } from "@somewear/model";
import _, { cloneDeep, isEqual } from "lodash";

import { IDeviceSettings, type TrackingInterval } from "./settings.model";

type SettingsState = {
	deviceSettings: Dict<DeviceSettingsResponse.AsObject>;
	deviceStatus: Dict<DeviceStatusResponse.AsObject>;
	deviceSettingsPending: Dict<DeviceSettingsResponse.AsObject>;
	deviceUserIds: Dict<string | undefined>;
	deviceTransfers: Dict<IDeviceTransfer>;
	globalTrackingInterval?: TrackingInterval;
	//globalGpsInterval?: number;
	loading: boolean;
	hasDevicesThatFailedToLoad: boolean;
	showArchived: boolean;
	showBorrowed: boolean;
	selectedDeviceDict: Dictionary<boolean>;
};

const initialState: SettingsState = {
	deviceSettings: {},
	deviceStatus: {},
	deviceUserIds: {},
	deviceSettingsPending: {},
	deviceTransfers: {},
	loading: true,
	hasDevicesThatFailedToLoad: false,
	showArchived: false,
	showBorrowed: true,
	selectedDeviceDict: {},
};

const nodeOnlySettings: (keyof DeviceSettings.AsObject)[] = [
	"buttonFunction",
	"radioMode",
	"enableBackhaul",
];

export function getOrCreateSettings(
	deviceSettings: Dict<DeviceSettingsResponse.AsObject>,
	serial: string,
): DeviceSettingsResponse.AsObject {
	let settingsResponse: DeviceSettingsResponse.AsObject;
	if (serial in deviceSettings) {
		settingsResponse = _.cloneDeep(deviceSettings[serial]);
		settingsResponse.serial = serial;
		settingsResponse.settings!.trackingOn = true;
	} else {
		settingsResponse = new DeviceSettingsResponse().toObject();
		settingsResponse.serial = serial;
		settingsResponse.settings = new DeviceSettings().toObject();
		settingsResponse.settings.trackingOn = true;
	}

	if (settingsResponse.settings?.enableBackhaul === OptionalBool.OPTIONALBOOLNONE) {
		settingsResponse.settings.enableBackhaul = OptionalBool.OPTIONALBOOLFALSE;
	}

	if (
		settingsResponse.settings?.enableSpeedAndCourseReporting === OptionalBool.OPTIONALBOOLNONE
	) {
		settingsResponse.settings.enableSpeedAndCourseReporting = OptionalBool.OPTIONALBOOLFALSE;
	}

	return settingsResponse;
}

function getSelectedSerials(state: SettingsState, excludedSerials: string[] = []): string[] {
	return Object.keys(state.selectedDeviceDict).filter(
		(serial) => !excludedSerials.includes(serial),
	);
}

function setTracking(state: SettingsState, serial: string, set: boolean) {
	if (serial in state.deviceSettingsPending) {
		state.deviceSettingsPending[serial].settings!.trackingOn = set;
	} else {
		state.deviceSettingsPending[serial] = getOrCreateSettings(state.deviceSettings, serial);
		state.deviceSettingsPending[serial].settings!.trackingOn = set;
		if (state.deviceSettingsPending[serial].settings!.trackingInterval === 0) {
			state.deviceSettingsPending[serial].settings!.trackingInterval = 1800;
		}
		if (state.deviceSettingsPending[serial].settings!.gpsInterval === 0) {
			state.deviceSettingsPending[serial].settings!.gpsInterval = 1800;
		}
	}
}

function removeOutdatedPendingChanges(state: SettingsState, serial: string) {
	if (
		isEqual(
			state.deviceSettingsPending[serial]?.settings,
			state.deviceSettings[serial]?.settings,
		)
	) {
		const newDeviceSettingsPending = cloneDeep(state.deviceSettingsPending);
		delete newDeviceSettingsPending[serial];

		state.deviceSettingsPending = newDeviceSettingsPending;
	}
}

function updateSettings<T extends keyof IDeviceSettings>({
	state,
	serial,
	key,
	value,
}: {
	state: SettingsState;
	serial: string;
	value: DeviceSettings.AsObject[T];
	key: T;
}) {
	if (nodeOnlySettings.includes(key) && !deviceHasNodeSerial(serial)) {
		console.warn(`Device ${serial} is not a node, skipping setting ${key}`);
		return;
	}

	if (serial in state.deviceSettingsPending) {
		state.deviceSettingsPending[serial].settings![key] = value;
		removeOutdatedPendingChanges(state, serial);
	} else {
		state.deviceSettingsPending[serial] = getOrCreateSettings(state.deviceSettings, serial);
		state.deviceSettingsPending[serial].settings![key] = value;
	}
}

function setDeviceSettings(
	state: SettingsState,
	serial: string,
	settings: DeviceSettingsResponse.AsObject,
) {
	const THREE_HOURS_IN_SECONDS = 10800;
	if (
		settings.type === DeviceSettingsEventType.SETTINGSCOMMANDAPPLIED &&
		settings.settingsCommand !== undefined &&
		(settings.settingsCommand.gpsinterval > THREE_HOURS_IN_SECONDS ||
			settings.settingsCommand.trackinginterval > THREE_HOURS_IN_SECONDS)
	) {
		// guard to avoid extreme tracking intervals from being applied
		// this was added to protect against a firmware bug that suggested the device had a tracking interval measured in months
		console.warn(`Unexpected settings update`, settings.settingsCommand);
		Sentry.captureException(`Unexpected settings update: greater than three hours`);
		return;
	}

	if (!(serial in state.deviceSettings)) {
		// initialize the settings
		state.deviceSettings[serial] = new DeviceSettingsResponse().toObject();
	}

	// When the last settings update happened on device. It has a different signature.
	if (
		settings.type === DeviceSettingsEventType.SETTINGSCOMMANDAPPLIED &&
		settings.settingsCommand !== undefined
	) {
		const command = settings.settingsCommand;

		state.deviceSettings[serial].settings = {
			gpsInterval: command.gpsinterval,
			batteryReporting: command.batteryReporting,
			enableAltitudeReporting: command.includeAltitude,
			trackingInterval: command.trackinginterval,
			trackingOn: false, // setting false so it can be flipped to true to initiate tracking
			// trackingOn: command.automaticTracking, // trackingOn is mapped to automaticTracking in the firmware
			sentTimestamp: command.sentTimestamp,
			enableBackhaul: command.backhaulEnabled,
			enableSpeedAndCourseReporting: command.includeSpeedAndCourse,
			lowSpeedFrequencyHz: command.lowSpeedFrequencyHz,
			highSpeedFrequencyHz: command.highSpeedFrequencyHz,
			radioMode: command.radioMode,
			radioPowerMode: mapSettingsCommandPowerModeToRadioPowerMode(command.radioPowerMode),
			buttonFunction: command.button1,
			meshVersionToDeploy: command.meshVersionToDeploy,
			iridiumNoSleep: toOptionalBool(command.nosleep),
			trackingOnOptional: OptionalBool.OPTIONALBOOLNONE,
			altitudeReportingOptional: OptionalBool.OPTIONALBOOLNONE,
			radioRegion: command.radioRegion,
			requestId: Math.floor(Math.random() * 10000000),
			satDisabled: command.satDisabled,
			ledDisabled: command.ledDisabled,
			hapticsDisabled: command.hapticsDisabled,
			lowDataModeEnabled: command.lowDataModeEnabled,
			lowBandwidthTrackingMultiplier: command.lowBandwidthTrackingMultiplier,
			inmarsatRxFrequencyHz: command.inmarsatRxFrequencyHz ?? 0,
			inmarsatTxFrequencyHz: command.inmarsatTxFrequencyHz ?? 0,
			inmarsatLeaseType: command.inmarsatLeaseType ?? InmarsatLeaseType.INMARSATLEASETYPENONE,
			outboundGcStrategy:
				command.outboundGcStrategy ?? OutboundGcStrategy.OUTBOUNDGCSTRATEGYUNSPECIFIED,
		};
	} else if (settings.settings !== undefined) {
		state.deviceSettings[serial].settings = settings.settings;
	} else {
		console.warn(`Unexpected settings update; type=${settings.type}`);
		Sentry.captureException(`Unexpected settings update: unknown type`);
		return;
	}
	state.deviceSettings[serial].timestamp = settings.timestamp;
	state.deviceSettings[serial].type = settings.type;
}

function setDeviceStatus(
	state: SettingsState,
	serial: string,
	status: DeviceStatusResponse.AsObject,
) {
	if (!(serial in state.deviceStatus)) {
		state.deviceStatus[serial] = new DeviceStatusResponse().toObject();
	}
	state.deviceStatus[serial].battery = status.battery;
	state.deviceStatus[serial].firmwareversion = status.firmwareversion;
	state.deviceStatus[serial].timestamp = status.timestamp;
	if (status.lastHeartbeat?.seconds !== undefined && status.lastHeartbeat.seconds > 0)
		state.deviceStatus[serial].lastHeartbeat = status.lastHeartbeat;
}

type SettingsCommandPowerMode = ValueOf<SettingsCommand.PowerModeMap>;
type RadioPowerMode = ValueOf<RadioPowerModeMap>;

/**
 * Maps the settings command's power mode values to the respective radio power mode.
 *
 * The settings command proto does not include a none value. This results in the indexes for low in the settings command's
 * power mode not aligning with the indexes of the radio power mode.
 */
function mapSettingsCommandPowerModeToRadioPowerMode(
	input: SettingsCommandPowerMode,
): RadioPowerMode {
	switch (input) {
		case SettingsCommand.PowerMode.POWERMODELOW:
			return RadioPowerMode.RADIOPOWERMODELOW;
		case SettingsCommand.PowerMode.POWERMODEMEDIUM:
			return RadioPowerMode.RADIOPOWERMODEMEDIUM;
		case SettingsCommand.PowerMode.POWERMODEHIGH:
			return RadioPowerMode.RADIOPOWERMODEHIGH;
		default:
			return RadioPowerMode.RADIOPOWERMODENONE;
	}
}

export const settingsSlice: Slice = createSlice({
	name: "settings",
	initialState,
	reducers: {
		apiDeviceEventSuccess(state, action: PayloadAction<DeviceEventResponse.AsObject[]>) {
			action.payload.forEach((event) => {
				if (event.settings !== undefined) {
					setDeviceSettings(state, event.settings.serial, event.settings);
				}
				if (event.status) {
					setDeviceStatus(state, event.status.serial, event.status);
				}
			});
		},
		toggleTracking(state, action: PayloadAction<{ serial: string; set: boolean }>) {
			setTracking(state, action.payload.serial, action.payload.set);
		},
		toggleBattery(state, action: PayloadAction<{ serial: string; set: boolean }>) {
			updateSettings({
				state,
				serial: action.payload.serial,
				value: action.payload.set,
				key: "batteryReporting",
			});
		},
		toggleEnableAltitudeReporting(
			state,
			action: PayloadAction<{ serial: string; set: boolean }>,
		) {
			updateSettings({
				state,
				serial: action.payload.serial,
				value: action.payload.set,
				key: "enableAltitudeReporting",
			});
		},
		toggleEnableBackhaulReporting(
			state,
			action: PayloadAction<{ serial: string; set: boolean }>,
		) {
			updateSettings({
				state,
				serial: action.payload.serial,
				value: toOptionalBool(action.payload.set),
				key: "enableBackhaul",
			});
		},
		toggleIridiumNoSleep(state, action: PayloadAction<{ serial: string; set: boolean }>) {
			updateSettings({
				state,
				serial: action.payload.serial,
				value: toOptionalBool(action.payload.set),
				key: "iridiumNoSleep",
			});
		},
		toggleEnableSpeedAndCourseReporting(
			state,
			action: PayloadAction<{ serial: string; set: boolean }>,
		) {
			updateSettings({
				state,
				serial: action.payload.serial,
				value: toOptionalBool(action.payload.set),
				key: "enableSpeedAndCourseReporting",
			});
		},
		setTrackingIntervals(
			state,
			action: PayloadAction<{
				serial: string;
				value: Pick<DeviceSettings.AsObject, "trackingInterval" | "gpsInterval"> & {
					lowBandwidthTrackingMultiplier?: DeviceSettings.AsObject["lowBandwidthTrackingMultiplier"];
				};
			}>,
		) {
			updateSettings({
				state,
				serial: action.payload.serial,
				value: action.payload.value.trackingInterval,
				key: "trackingInterval",
			});
			updateSettings({
				state,
				serial: action.payload.serial,
				value: action.payload.value.gpsInterval,
				key: "gpsInterval",
			});

			if (action.payload.value.lowBandwidthTrackingMultiplier !== undefined) {
				updateSettings({
					state,
					serial: action.payload.serial,
					value: action.payload.value.lowBandwidthTrackingMultiplier,
					key: "lowBandwidthTrackingMultiplier",
				});
			}
		},
		setButtonFunction(
			state,
			action: PayloadAction<{
				serial: string;
				value: ButtonFunctionMap[keyof ButtonFunctionMap];
			}>,
		) {
			updateSettings({
				state,
				serial: action.payload.serial,
				value: action.payload.value,
				key: "buttonFunction",
			});
		},
		bulkUpdateButtonFunctionForSelected(
			state,
			action: PayloadAction<ButtonFunctionMap[keyof ButtonFunctionMap]>,
		) {
			Object.keys(state.selectedDeviceDict).forEach((serial) => {
				updateSettings({
					state,
					serial,
					value: action.payload,
					key: "buttonFunction",
				});
			});
		},
		bulkUpdateAutoTrackingForSelected(state, action: PayloadAction<boolean>) {
			Object.keys(state.selectedDeviceDict).forEach((serial) => {
				setTracking(state, serial, action.payload);
			});
		},
		bulkUpdateBatteryReportingForSelected(state, action: PayloadAction<boolean>) {
			Object.keys(state.selectedDeviceDict).forEach((serial) => {
				updateSettings({
					state,
					serial,
					value: action.payload,
					key: "batteryReporting",
				});
			});
		},
		bulkUpdateAltitudeReportingForSelected(
			state,
			action: PayloadAction<{ value: boolean; excludedSerials: string[] }>,
		) {
			getSelectedSerials(state, action.payload.excludedSerials).forEach((serial) => {
				updateSettings({
					state,
					serial,
					value: action.payload.value,
					key: "enableAltitudeReporting",
				});
			});
		},
		bulkUpdateBackhaulReportingForSelected(state, action: PayloadAction<boolean>) {
			Object.keys(state.selectedDeviceDict).forEach((serial) => {
				updateSettings({
					state,
					serial,
					value: toOptionalBool(action.payload),
					key: "enableBackhaul",
				});
			});
		},
		bulkUpdateEnduranceModeForSelected(state, action: PayloadAction<boolean>) {
			Object.keys(state.selectedDeviceDict).forEach((serial) => {
				// Endurance Mode ON = noSleep OFF (inverted semantics)
				updateSettings({
					state,
					serial,
					value: toOptionalBool(!action.payload),
					key: "iridiumNoSleep",
				});
			});
		},
		bulkUpdateSpeedAndCourseReportingForSelected(
			state,
			action: PayloadAction<{ value: boolean; excludedSerials: string[] }>,
		) {
			getSelectedSerials(state, action.payload.excludedSerials).forEach((serial) => {
				updateSettings({
					state,
					serial,
					value: toOptionalBool(action.payload.value),
					key: "enableSpeedAndCourseReporting",
				});
			});
		},
		bulkUpdateTrackingIntervalsForSelected(
			state,
			action: PayloadAction<
				Pick<DeviceSettings.AsObject, "trackingInterval" | "gpsInterval"> & {
					lowBandwidthTrackingMultiplier?: DeviceSettings.AsObject["lowBandwidthTrackingMultiplier"];
				}
			>,
		) {
			Object.keys(state.selectedDeviceDict).forEach((serial) => {
				updateSettings({
					state,
					serial,
					value: action.payload.trackingInterval,
					key: "trackingInterval",
				});
				updateSettings({
					state,
					serial,
					value: action.payload.gpsInterval,
					key: "gpsInterval",
				});
				if (action.payload.lowBandwidthTrackingMultiplier !== undefined) {
					updateSettings({
						state,
						serial,
						value: action.payload.lowBandwidthTrackingMultiplier,
						key: "lowBandwidthTrackingMultiplier",
					});
				}
			});
		},
		revertChanges(state) {
			state.deviceSettingsPending = {};
		},
		setShowArchived(state, action: PayloadAction<boolean>) {
			state.showArchived = action.payload;
		},
		setShowBorrowed(state, action: PayloadAction<boolean>) {
			state.showBorrowed = action.payload;
		},
		setDeviceSelected(state, action: PayloadAction<{ serial: string; selected: boolean }>) {
			state.selectedDeviceDict[action.payload.serial] = action.payload.selected;
		},
		setSelectedDeviceDict(state, action: PayloadAction<Dictionary<boolean>>) {
			state.selectedDeviceDict = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(emitUserAccountChangeFromServer, (state, action) => {
			const user = action.payload;
			Object.keys(state.deviceUserIds).forEach((serial) => {
				if (state.deviceUserIds[serial] === user.id) {
					state.deviceUserIds[serial] = action.payload.id;
				}
			});
			// adapter.upsertOne(state, user);
		});
		builder.addCase(emitAddUserAccountFromServer, (state, action) => {
			console.log("added user");
			// maybe the user should be added to device users
		});
		builder.addCase(deviceActions.fetchSummary.fulfilled, (state, action) => {
			state.loading = false;
			state.hasDevicesThatFailedToLoad = action.payload.data.hasDevicesThatFailedToLoad;
			const devices = action.payload.data.devicesList;
			devices.forEach((event) => {
				if (event.settings !== undefined) {
					setDeviceSettings(state, event.serial, event.settings);
				}
				if (event.status) {
					setDeviceStatus(state, event.serial, event.status);
				}
				state.deviceUserIds[event.serial] = event.userId;
			});
		});
		builder.addCase(deviceActions.fetchSummary.rejected, (state) => {
			state.loading = false;
		});
		builder.addCase(deviceActions.submitSettings.fulfilled, (state, action) => {
			state.deviceSettingsPending = {};
			const events = action.payload.data.eventsList;
			events.forEach((event) => {
				if (event.settings !== undefined) {
					setDeviceSettings(state, event.settings.serial, event.settings);
				}
				if (event.status) {
					setDeviceStatus(state, event.status.serial, event.status);
				}
			});
		});
		builder.addCase(deviceTransferActions.applyQueuedTransfers.fulfilled, (state, action) => {
			const devices = action.payload.data.mapNotNull((it) => it.device);
			devices.forEach((device) => {
				state.deviceUserIds[device.serial] = device.userAccountId;
			});
		});
		builder.addCase(deviceTransferActions.unaryAssign.fulfilled, (state, action) => {
			const device = action.payload.data.device;
			if (device === undefined) return;
			state.deviceUserIds[device.serial] = device.userAccountId;
		});
		builder.addCase(deviceTransferActions.bulkAssign.fulfilled, (state, action) => {
			const devices = action.payload.data.mapNotNull((it) => it.device);
			devices.forEach((device) => {
				state.deviceUserIds[device.serial] = device.userAccountId;
			});
		});
		builder.addCase(apiDeviceRecordUpdate, (state, action) => {
			if (action.payload.asset !== undefined) {
				state.deviceUserIds[action.payload.record.serial] = action.payload.asset.id;
			}
		});
	},
});

export const {
	apiDeviceEventSuccess,
	toggleTracking,
	toggleBattery,
	toggleEnableAltitudeReporting,
	toggleEnableBackhaulReporting,
	toggleIridiumNoSleep,
	toggleEnableSpeedAndCourseReporting,
	setTrackingIntervals,
	bulkUpdateEnduranceModeForSelected,
	bulkUpdateSpeedAndCourseReportingForSelected,
	bulkUpdateButtonFunctionForSelected,
	bulkUpdateAutoTrackingForSelected,
	bulkUpdateBatteryReportingForSelected,
	bulkUpdateAltitudeReportingForSelected,
	bulkUpdateTrackingIntervalsForSelected,
	bulkUpdateBackhaulReportingForSelected,
	setButtonFunction,
	revertChanges,
	setShowArchived,
	setShowBorrowed,
	setSelectedDeviceDict,
	setDeviceSelected,
} = settingsSlice.actions;
